<script setup>
import { ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import AppLayout from '@/Layouts/AppLayout.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import TextInput from '@/Components/TextInput.vue'
import PaginationLinks from '@/Components/PaginationLinks.vue'

const props = defineProps({
    taxes: Object,
    pageSearchQuery: String,
})

const searchQuery = ref(props.pageSearchQuery || '')

const makeSearchRequest = _.debounce(() => {
    Inertia.get(route('admin.tax.index'), { query: searchQuery.value }, { preserveScroll: true })
}, 300)

const toggleStatus = (tax) => {
    Inertia.put(
        route('admin.tax.update', tax.tax_id),
        {
            tax_name: tax.tax_name,
            tax_amount: tax.tax_amount,
            status: tax.status ? 0 : 1,
        },
        { preserveScroll: true }
    )
}

</script>

<template>
    <AppLayout title="Taxes">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">Taxes</h2>
        </template>

        <div class="mx-auto py-10 sm:px-6 lg:px-8">
            <div class="mb-4 flex justify-between items-center">
                <TextInput
                    v-model="searchQuery"
                    placeholder="Search taxes by name..."
                    @input="makeSearchRequest"
                    class="w-1/2"
                />
                <a :href="route('admin.tax.create')">
                    <PrimaryButton>Add Tax</PrimaryButton>
                </a>
            </div>

            <table class="min-w-full w-full text-left text-sm">
                <thead class="border-b">
                    <tr>
                        <th class="px-4 py-2">Name</th>
                        <th class="px-4 py-2">Amount</th>
                        <th class="px-4 py-2">Status</th>
                        <th class="px-4 py-2">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="t in taxes.data" :key="t.tax_id" class="border-b">
                        <td class="px-4 py-2">{{ t.tax_name }}</td>
                        <td class="px-4 py-2">{{ Number(t.tax_amount).toFixed(2) }}</td>
                        <td class="px-4 py-2">
                            <span :class="t.status ? 'text-green-700' : 'text-gray-500'">
                                {{ t.status ? 'Enabled' : 'Disabled' }}
                            </span>
                        </td>
                        <td class="px-4 py-2 space-x-3">
                            <a :href="route('admin.tax.show', t.tax_id)" class="fa fa-eye" title="View" />
                            <a :href="route('admin.tax.edit', t.tax_id)" class="fa fa-pen" title="Edit" />

                            <button
                                @click="toggleStatus(t)"
                                :class="[
                                    'fa cursor-pointer transition-colors',
                                    t.status
                                        ? 'fa-toggle-on text-green-600 hover:text-green-800'
                                        : 'fa-toggle-off text-gray-500 hover:text-gray-700'
                                ]"
                                :title="t.status ? 'Disable' : 'Enable'"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <div class="mt-4">
                <PaginationLinks :pagination="taxes" />
            </div>
        </div>
    </AppLayout>
</template>